/*
 * Copyright (c) Nexign, JSC, 2021-2025.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Alexander Golshtein
 * @author Nikolay Sviridov
 * @version 1.38.0
 * @since 1.14.1
 */

import { Fragment, useCallback, useEffect } from 'react';

import { connect, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';


import { EBasketType } from '@modules/app/appConstants';
import { TranslationFileNamesEnum } from '@modules/internationalization/constants';
import {
    makeSelectTranslationCommon,
    makeSelectTranslationWizardBillsDoc,
} from '@modules/internationalization/intSelectors';
import { IWizardBillsDocTranslation } from '@modules/internationalization/types';
import { ECommonTranslationPages } from '@modules/internationalization/types/common/pages';
import { ELogBusinessEventName, ELogBusinessEventType, logBusinessEvent } from '@modules/logging';
import { ILogBusinessEventPayload } from '@modules/logging/loggingTypes';
import { EWizardName, WIZARD_ALL_ENTITIES } from '@modules/wizard/wizardConstants';
import { IWizardState } from '@modules/wizard/wizardReducer';
import { makeSelectWizardStepName } from '@modules/wizard/wizardSelectors';
import { IRootReducer } from '@reducers/rootReducer';
import { IRouteParams } from '@routes/constants/routesConstants';

import Title from '../../../components/common/Title/Title';
import withAsyncStateManagement from '../../../modules/asyncStateManagment/WithAsyncStateManagement';
import PageLocale from '../../../modules/internationalization/components/PageLocale';
import Wizard, { IWizardProps } from '../../../modules/wizard/components/Wizard';
import { wizardBillsDocHandleNextAction } from '../wizardBillsDocActions';
import { EWizardBillsDocSteps } from '../wizardBillsDocConsant';
import wizardBillsDocReducer, { IWizardBillsDocReducer } from '../wizardBillsDocReducer';
import { makeSelectWizardBillsDocIsStepValid } from '../wizardBillsDocSelectors';
import WizardBillsDocStepChoose from './WizardBillsDocStepChoose';
import WizardBillsDocStepConfirmation from './WizardBillsDocStepConfirmation';


const WizardBillsDoc = ({
                            basketType,
                            isStepValid,
                            stepName,
                            translationBillsDoc,
                            handleNext,
                            logBusinessEvent,
                        }: IConnectedProps & IDispatchProps & Pick<IWizardProps, 'basketType'>) => {

    const { clientId } = useParams<IRouteParams>();

    const translationCommon = useSelector(makeSelectTranslationCommon());
    const { instructionsLinks: { repsBasic } } = translationCommon;

    const wizardBasketType = basketType === EBasketType.BILLS || basketType === EBasketType.BILLS_UNPAID
        ? EBasketType.BILLS
        : EBasketType.BILLS_ACCOUNT;

    const renderStep = useCallback(() => {
        switch (stepName) {
            case EWizardBillsDocSteps.StepChoose:
                return <WizardBillsDocStepChoose/>;
            case EWizardBillsDocSteps.StepConfirmation:
                return <WizardBillsDocStepConfirmation/>;
        }
    }, [stepName]);

    useEffect(() => {
        switch (stepName) {
            case EWizardBillsDocSteps.StepChoose:
                logBusinessEvent({
                    eventName: ELogBusinessEventName.B2B_BILLS_REPORTS_MASTER_ON_STEP_1,
                    type: ELogBusinessEventType.common,
                });
                break;
            case EWizardBillsDocSteps.StepConfirmation:
                logBusinessEvent({
                    eventName: ELogBusinessEventName.B2B_BILLS_REPORTS_MASTER_ON_STEP_2,
                    type: ELogBusinessEventType.common,
                });
        }
    }, [logBusinessEvent, stepName])

    return (
        <Fragment>
            <PageLocale lazy
                        fileNames={[
                            TranslationFileNamesEnum.wizardBillsDoc,
                            TranslationFileNamesEnum.wizard,
                        ]}/>

            <Title pageName={ECommonTranslationPages.wizardBillsDoc}/>

            <Wizard
                basketType={wizardBasketType}
                entityId={clientId ? parseInt(clientId) : WIZARD_ALL_ENTITIES}
                instructionLink={repsBasic}
                isNextDisabled={!isStepValid}
                name={EWizardName.BILLS_DOC}
                translationProps={{
                    error: translationBillsDoc.error,
                    steps: translationBillsDoc.steps,
                    title: translationBillsDoc.title,
                }}
                onWizardNextHandler={handleNext}
            >
                {renderStep()}
            </Wizard>
        </Fragment>
    );
};

export interface IConnectedProps extends Pick<IWizardState, 'stepName'>, Pick<IWizardBillsDocReducer, 'isStepValid'> {
    translationBillsDoc: IWizardBillsDocTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    isStepValid: makeSelectWizardBillsDocIsStepValid(),
    stepName: makeSelectWizardStepName(),
    translationBillsDoc: makeSelectTranslationWizardBillsDoc(),
});

export interface IDispatchProps {
    handleNext: () => void;
    logBusinessEvent: (payload: ILogBusinessEventPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    handleNext: () => dispatch(wizardBillsDocHandleNextAction()),
    logBusinessEvent: (payload) => dispatch(logBusinessEvent(payload)),
});

export default withAsyncStateManagement(
    connect(mapStateToProps, mapDispatchToProps)(WizardBillsDoc),
    { reducer: { wizardBillsDoc: wizardBillsDocReducer } },
);
